/* eslint-disable no-unused-vars */
import { Input } from "antd"
import React, { useCallback } from "react"
import styled from "styled-components"
import { CharacterCounter } from "../../../utils"
import useDeviceTheme from "../../../useDeviceTheme"
export interface TextFieldProps {
  placeholder?: string
  type?: string
  onChange?: any
  className?: any
  value?: any
  disabled?: boolean
  prefix?: React.ReactNode
  rows?: number
  fullWidth?: boolean
  width?: string
  height?: string
  showCounting?: boolean
  maxLength?: number
  bordercolor?: string
  bgcolor?: string
  color?: string
  error?: any
  name?: string
  borderradius?: string
  placeholdercolor?: string
  label?: string
  addonBefore?: string
  disableboxshadow?: boolean
  required?: boolean
  indent?: string
  ref?: any
  min?: number
  autoComplete?: string
  onKeyUp?: (val: any) => void
  onPressEnter?: (e: any) => void
  onBlur?: (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void
}

const InputStyled = styled(Input)`
  font-family: Lato, sans-serif;
  border-radius: ${({ borderradius }: TextFieldProps) => borderradius || "4px"};
  background-color: ${({ bgcolor }: TextFieldProps) =>
    bgcolor || "transparent"};
  height: ${({ height }: TextFieldProps) => height || "60px"};
  color: ${(props) => props.theme.sameBase};
  border: ${(props: any) =>
    props.bordercolor
      ? `1px solid ${props.bordercolor}`
      : `1px solid ${props.theme.textFieldBorderColor}`} !important;
  width: ${({ width, fullWidth }: TextFieldProps) =>
    fullWidth ? "100%" : width || "auto"};
  text-indent: ${({ indent }: TextFieldProps) => indent || "0px"};
  ::placeholder {
    color: ${(props) =>
      (props.placeholdercolor && props.placeholdercolor) ||
      props.theme.placeholder};
    text-indent: ${({ indent }: TextFieldProps) => indent || "0px"};
    vertical-align: middle;
    @media (max-width: 1023px) {
      font-size: 14px;
    }
  }
  box-shadow: ${({ disableboxshadow, isDarkTheme }) =>
    disableboxshadow || !isDarkTheme
      ? "none"
      : "inset 0px 2px 5px rgba(0, 0, 0, 20%)"} !important;
`

export const ErrorStyled = styled.span`
  font-size: 12px;
  color: ${(props) => props.theme.alert};
  margin-top: 2px;
  margin-left: 2px;
`

const CountingStyled = styled.div<{ isRed: boolean }>`
  font-size: 12px;
  color: ${(props) => props.isRed && props.theme.alert};
`

export const ErrorCounterWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 2px;
`

const TextFieldWrapperStyled = styled.div`
  display: flex;
  flex-direction: column;
  width: ${({ width, fullWidth }: TextFieldProps) =>
    fullWidth ? "100%" : width || "auto"};
`

const LabelContainer = styled.div`
  display: flex;
  margin-bottom: 8px;
`

const Label = styled.div`
  ${(props) => props.theme.typography.label};
  margin-right: 10px;
  font-weight: 500;
  color: ${(props) => props.theme.sameBase};
  @media (max-width: 1023px) {
    font-size: 14px;
  }
`

const RequiredLabel = styled.div`
  ${(props) => props.theme.typography.PackDesc};
  color: #f5222d;
  min-width: 40px;
  height: 22px;
  display: flex;
  justify-content: center;
  background: #fff1f0;
  border: 1px solid #ffa39e;
  border-radius: 2px;
  align-items: center;
`

const TextField: React.FC<TextFieldProps> = React.forwardRef((props, ref) => {
  const {
    showCounting,
    value,
    maxLength,
    type,
    rows,
    className,
    label,
    required,
    prefix,
    min,
    ...rest
  } = props
  const { TextArea } = Input
  const isDarkTheme = useDeviceTheme()
  const countingUI = useCallback(() => {
    return (
      showCounting && (
        <CountingStyled isRed={value?.length >= maxLength}>
          {`${
            value ? CharacterCounter(value) : 0
          }/${maxLength.toLocaleString()}`}
        </CountingStyled>
      )
    )
  }, [value, maxLength])

  const textFieldUI = () => {
    switch (type) {
      case "password":
        return (
          <TextFieldWrapperStyled className={className} width={props.width}>
            {label && (
              <LabelContainer>
                <Label>{label}</Label>
                {required && <RequiredLabel>{"必須"}</RequiredLabel>}
              </LabelContainer>
            )}
            <InputStyled
              isDarkTheme={isDarkTheme}
              prefix={prefix}
              {...rest}
              type={"password"}
              placeholder={props.placeholder}
            />
            <ErrorCounterWrapper>
              {countingUI()}
              {props.error ? <ErrorStyled>{props.error}</ErrorStyled> : <div />}
            </ErrorCounterWrapper>
          </TextFieldWrapperStyled>
        )
      case "textarea":
        return (
          <TextFieldWrapperStyled className={className} width={props.width}>
            {label && (
              <LabelContainer>
                <Label>{label}</Label>
                {required && <RequiredLabel>{"必須"}</RequiredLabel>}
              </LabelContainer>
            )}
            <InputStyled
              isDarkTheme={isDarkTheme}
              as={TextArea}
              value={value}
              autoSize={{ minRows: rows }}
              placeholder={props.placeholder}
              maxLength={maxLength}
              {...rest}
            />
            <ErrorCounterWrapper>
              {props.error ? <ErrorStyled>{props.error}</ErrorStyled> : <div />}
              {/* {countingUI()} */}
            </ErrorCounterWrapper>
          </TextFieldWrapperStyled>
        )
      case "number":
        return (
          <TextFieldWrapperStyled className={className} width={props.width}>
            {label && (
              <LabelContainer>
                <Label>{label}</Label>
                {required && <RequiredLabel>{"必須"}</RequiredLabel>}
              </LabelContainer>
            )}
            <InputStyled
              isDarkTheme={isDarkTheme}
              prefix={prefix}
              value={value}
              type={"number"}
              min={min}
              maxLength={maxLength}
              {...rest}
            />
            <ErrorCounterWrapper>
              {props.error ? <ErrorStyled>{props.error}</ErrorStyled> : <div />}
              {countingUI()}
            </ErrorCounterWrapper>
          </TextFieldWrapperStyled>
        )

      default:
        return (
          <TextFieldWrapperStyled className={className} width={props.width}>
            {label && (
              <LabelContainer>
                <Label>{label}</Label>
                {required && <RequiredLabel>{"必須"}</RequiredLabel>}
              </LabelContainer>
            )}
            <InputStyled
              isDarkTheme={isDarkTheme}
              ref={ref}
              prefix={prefix}
              value={value}
              maxLength={maxLength}
              placeholder={props.placeholder}
              {...rest}
            />
            <ErrorCounterWrapper>
              {props.error ? <ErrorStyled>{props.error}</ErrorStyled> : <div />}
              {countingUI()}
            </ErrorCounterWrapper>
          </TextFieldWrapperStyled>
        )
    }
  }
  return textFieldUI()
})

export { TextField }
